/**
 * Life4Earth V2 — React hook for cross-project user role assignments.
 *
 * Loads active and completed role assignments for a user across all projects.
 * Used on the Impact tab to show "My Active Roles" and role history.
 *
 * Pattern follows useApiImpactSignature.ts.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchUserActiveRoles,
  fetchUserCompletedRoles,
} from "./impactSignature";
import type { UserRoleAssignmentResponse } from "./impactSignature";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

// ── Result shape ────────────────────────────────────────────

export interface UseApiUserRolesResult {
  /** Active assignments (status: active, pending_verification, pending_escalated_review). */
  activeRoles: UserRoleAssignmentResponse[];
  /** Completed assignments. */
  completedRoles: UserRoleAssignmentResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  /** Force a refetch (e.g. after marking a role complete). */
  refetch: () => void;
}

interface CachedUserRolesData {
  activeRoles: UserRoleAssignmentResponse[];
  completedRoles: UserRoleAssignmentResponse[];
}

// ── Hook ────────────────────────────────────────────────────

/**
 * Load active and completed role assignments for a user.
 * Pass null/undefined userGuid to skip loading.
 */
export function useApiUserRoles(
  userGuid: string | null | undefined
): UseApiUserRolesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiUserRolesResult>({
    activeRoles: [],
    completedRoles: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const cacheKey = userGuid
    ? `userRoles:${userGuid}`
    : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;

    try {
      const cached = await fetchForKey(
        cacheKey!,
        async (): Promise<CachedUserRolesData> => {
          const [activeRoles, completedRoles] = await Promise.all([
            fetchUserActiveRoles(userGuid),
            fetchUserCompletedRoles(userGuid),
          ]);
          return { activeRoles, completedRoles };
        },
        { force: true }
      );

      if (!mountedRef.current) return;

      setData({
        activeRoles: cached.activeRoles,
        completedRoles: cached.completedRoles,
        loading: false,
        error: null,
        source: "api",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load user roles",
        source: "api",
        refetch,
      }));
    }
  }, [userGuid, cacheKey]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) {
      refetch();
    } else {
      setData({
        activeRoles: [],
        completedRoles: [],
        loading: false,
        error: null,
        source: "empty",
        refetch,
      });
    }
    return () => {
      mountedRef.current = false;
    };
  }, [userGuid, refetch]);

  // Listen for cache invalidation events (SignalR-driven)
  // Invalidates on both userRoles and projectRoles changes
  useEffect(() => {
    if (!cacheKey) return;

    const handleCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Refetch when our specific key or any projectRoles key is invalidated
      // (assignment changes on a project will invalidate projectRoles:{guid})
      if (
        detail?.key === cacheKey ||
        (typeof detail?.key === "string" && detail.key.startsWith("projectRoles:"))
      ) {
        refetch();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
  }, [cacheKey, refetch]);

  return data;
}
